import { FC, useCallback, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { FiRefreshCw } from "react-icons/fi";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Skeleton,
  SwipeableDrawer,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { CloseIcon } from "../icons";
import AttachmentIcon from "../icons/AttachmentIcon";
import AttachedFileIcon from "../icons/AttachedFile";
import { CheckIcon } from "../icons/CheckIcon";

import { useTicketStore } from "../../hooks/useTicketStore";
import { useChatCategoryStore } from "../../hooks/useChatCategoryStore";
import useNotifier from "../../hooks/useNotifier";
import SidebarDelimiter from "../SidebarDelimiter";
import ResizePointerShim from "../ui/ResizePointerShim";
import { formatInstant } from "../helpers/dataHelper";
import { isTicketStateClosed, matchTicketDisplayStatus } from "./ticketDisplay";
import TicketStatusChip from "./TicketStatusChip";
import TicketsRenderer from "./TicketsRenderer";
import CoreTextarea from "../ui/core/CoreTextarea";
import { TICKET_REPLY_ATTACHMENT_LIMITS } from "../../config/constants";
import { useOrgLinkBranding } from "../../contexts/AuthContext";

interface TicketDetailPanelProps {
  ticketId: number | null;
  onClose: () => void;
}

const { maxFiles, maxTotalMb, acceptLabel, allowedExtensions } =
  TICKET_REPLY_ATTACHMENT_LIMITS;
const maxTotalBytes = maxTotalMb * 1024 * 1024;

const getFileExtension = (fileName: string): string => {
  const lastDot = fileName.lastIndexOf(".");
  return lastDot === -1 ? "" : fileName.slice(lastDot).toLowerCase();
};

// Browsers don't expose file paths, so name + size + lastModified is the
// closest identity we have for spotting the same file picked twice.
const getFileKey = (file: File): string =>
  `${file.name}::${file.size}::${file.lastModified}`;

const TicketDetailPanel: FC<TicketDetailPanelProps> = observer(
  ({ ticketId, onClose }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const ticketStore = useTicketStore();
    const { whitelabel } = useOrgLinkBranding();
    const { showError } = useNotifier();
    const {
      sidePanelWidth,
      isSidePanelResizing,
      setSidePanelWidth,
      setIsSidePanelResizing,
    } = useChatCategoryStore();
    
    const [replyBody, setReplyBody] = useState("");
    const [pendingAttachments, setPendingAttachments] = useState<File[]>([]);
    const [attachmentError, setAttachmentError] = useState<string | null>(null);

    const handleMouseMove = useCallback((e: MouseEvent) => {
      const containerRef = document.getElementById("tickets-page-container");
      if (!containerRef) return;

      const containerRect = containerRef.getBoundingClientRect();
      const newWidth = containerRect.right - e.clientX;
      const newPercent = (newWidth / containerRect.width) * 100;

      setSidePanelWidth(newPercent);
    }, []);

    const handleMouseUp = useCallback(() => {
      setIsSidePanelResizing(false);
    }, []);

    useEffect(() => {
      if (!isSidePanelResizing) return;

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
    }, [isSidePanelResizing, handleMouseMove, handleMouseUp]);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
      e.preventDefault();
      setIsSidePanelResizing(true);
    }, []);

    useEffect(() => {
      if (ticketId) {
        ticketStore.loadTicket(ticketId);
        setReplyBody("");
        setPendingAttachments([]);
        setAttachmentError(null);
      }
    }, [ticketId]);

    // Auto-dismiss the rejection message so it doesn't linger indefinitely.
    useEffect(() => {
      if (!attachmentError) return;
      const timer = setTimeout(() => setAttachmentError(null), 5000);
      return () => clearTimeout(timer);
    }, [attachmentError]);

    const scrollContainerRef = useRef<HTMLDivElement>(null);

    if (!ticketId) return null;

    const ticket = ticketStore.activeTicket;
    const status = ticket?.state
      ? (matchTicketDisplayStatus(ticket.state) ?? ticket.state)
      : null;
    const isClosed = isTicketStateClosed(ticket?.state);
    const isTicketLoading = ticketStore.isDetailLoading || !ticket;

    const handleClose = () => {
      onClose();
    };

    const handleAttachmentChange = (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const { files } = event.target;
      if (!files || files.length === 0) return;
      const selectedFiles = Array.from(files);
      event.target.value = "";

      const remainingSlots = maxFiles - pendingAttachments.length;
      if (remainingSlots <= 0) return;

      const accepted: File[] = [];
      let rejection: {
        name: string;
        reason: "type" | "size" | "duplicate";
      } | null = null;
      let totalBytes = pendingAttachments.reduce((sum, f) => sum + f.size, 0);
      const seenKeys = new Set(pendingAttachments.map(getFileKey));

      for (const file of selectedFiles) {
        if (accepted.length >= remainingSlots) break;

        const key = getFileKey(file);
        if (seenKeys.has(key)) {
          rejection = rejection ?? { name: file.name, reason: "duplicate" };
          continue;
        }

        if (!allowedExtensions.includes(getFileExtension(file.name))) {
          rejection = rejection ?? { name: file.name, reason: "type" };
          continue;
        }
        if (totalBytes + file.size > maxTotalBytes) {
          rejection = rejection ?? { name: file.name, reason: "size" };
          continue;
        }
        totalBytes += file.size;
        seenKeys.add(key);
        accepted.push(file);
      }

      if (accepted.length > 0) {
        setPendingAttachments((prev) => [...prev, ...accepted]);
      }

      setAttachmentError(
        rejection
          ? rejection.reason === "type"
            ? `"${rejection.name}" isn't supported. Use ${acceptLabel}`
            : rejection.reason === "duplicate"
              ? `"${rejection.name}" is already attached`
              : `"${rejection.name}" would take the attachments over ${maxTotalMb} MB`
          : null,
      );
    };

    const handleRemoveAttachment = (index: number) => {
      setPendingAttachments((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSendReply = async () => {
      if (!replyBody.trim() || !ticket) return;
      const response = await ticketStore.createReply(
        ticket.id,
        replyBody,
        pendingAttachments,
      );
      if (response?.success) {
        setReplyBody("");
        setPendingAttachments([]);
      } else {
        setAttachmentError(response?.error || "Couldn't send the reply");
      }
    };

    const handleReopen = async () => {
      if (!ticket) return;
      try {
        const response = await ticketStore.reopenTicket(ticket.id);
        if (!response?.success) {
          showError(response?.error || "Failed to reopen ticket");
        }
      } catch {
        showError("Failed to reopen ticket");
      }
    };

    const panelContent = (
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 1.5,
            flexShrink: 0,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
          >
            {!isTicketLoading ? (
              <>
                <Typography
                  noWrap
                  sx={{
                    fontWeight: 700,
                    fontSize: "17px",
                    color: "text.primary",
                  }}
                >
                  #{ticket.number}
                </Typography>
                <Typography sx={{ color: "text.secondary" }}>
                  &middot;
                </Typography>
                <Typography noWrap sx={{ color: "text.secondary" }}>
                  {ticket.group || "-"}
                </Typography>
                {status ? <TicketStatusChip status={status} /> : null}
              </>
            ) : (
              <Skeleton variant="text" width={180} height={28} />
            )}
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              flexShrink: 0,
              "&:hover": {
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                "& svg": { width: 20, height: 20 },
              }}
            >
              <CloseIcon className="" />
            </Box>
          </IconButton>
        </Box>

        <Box
          ref={scrollContainerRef}
          sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 3, py: 2.5 }}
        >
          {isTicketLoading ? (
            <>
              <Skeleton variant="text" width={200} height={30} />
              <Skeleton
                variant="rectangular"
                height={120}
                sx={{ mt: 1.5, borderRadius: "8px" }}
              />
              <Skeleton
                variant="rectangular"
                height={80}
                sx={{ mt: 1.5, borderRadius: "8px" }}
              />
            </>
          ) : ticketStore.error ? (
            <Typography color="error">{ticketStore.error}</Typography>
          ) : (
            <>
              <TicketsRenderer
                ticket={ticket}
                scrollContainerRef={scrollContainerRef}
              />

              {isClosed ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    backgroundColor: "alert_success.light",
                    color: "alert_success.main",
                    borderRadius: "8px",
                    px: 2,
                    py: 1.25,
                    mt: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      border: "1.5px solid",
                      borderColor: "alert_success.main",
                      flexShrink: 0,
                    }}
                  >
                    <CheckIcon size={11} />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {status} on{" "}
                    {formatInstant(ticket.updated_at, { dateOnly: true })} by{" "}
                    {whitelabel ? "Support" : "Reve Support"}
                  </Typography>
                </Box>
              ) : null}
            </>
          )}
        </Box>

        {isTicketLoading ? null : (
          <Box
            sx={{
              px: 3,
              py: 2,
              flexShrink: 0,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            {isClosed ? (
              <Button
                variant="contained"
                fullWidth
                onClick={handleReopen}
                disabled={ticketStore.reopenLoading}
                startIcon={
                  ticketStore.reopenLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <FiRefreshCw />
                  )
                }
                sx={{ py: 1.25 }}
              >
                {ticketStore.reopenLoading ? "Reopening..." : "Reopen ticket"}
              </Button>
            ) : (
              <>
                <CoreTextarea
                  placeholder="Send a message"
                  value={replyBody}
                  disabled={ticketStore.replyLoading}
                  minRows={2}
                  maxRows={4}
                  onChange={(e) => setReplyBody(e.target.value)}
                  style={{ resize: "none" }}
                />

                {pendingAttachments.length > 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.75,
                      mt: 1.5,
                    }}
                  >
                    {pendingAttachments.map((file, index) => (
                      <Box
                        key={`${file.name}-${index}`}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: "8px",
                          px: 1.5,
                          py: 0.75,
                          backgroundColor: "background.paper",
                        }}
                      >
                        <AttachedFileIcon />
                        <Typography
                          noWrap
                          title={file.name}
                          variant="body2"
                          sx={{ flex: 1, minWidth: 0 }}
                        >
                          {file.name}
                        </Typography>
                        <IconButton
                          size="small"
                          disabled={ticketStore.replyLoading}
                          aria-label="Remove attachment"
                          onClick={() => handleRemoveAttachment(index)}
                          sx={{ color: "text.secondary", flexShrink: 0 }}
                        >
                          <CloseIcon size={14} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                ) : null}

                {attachmentError ? (
                  <Typography
                    variant="caption"
                    sx={{ color: "error.main", display: "block", mt: 1 }}
                  >
                    {attachmentError}
                  </Typography>
                ) : (
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", display: "block", mt: 1 }}
                  >
                    {acceptLabel} · up to {maxFiles} files, {maxTotalMb} MB
                    total
                  </Typography>
                )}

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mt: 1.5,
                    gap: 2,
                  }}
                >
                  <Button
                    component="label"
                    variant="outlined"
                    color="inherit"
                    startIcon={<AttachmentIcon />}
                    disabled={
                      ticketStore.replyLoading ||
                      pendingAttachments.length >= maxFiles
                    }
                    sx={{
                      color: "text.secondary",
                      borderColor: "divider",
                      fontWeight: 500,
                      width: "100%",
                      "&:hover": {
                        color: "primary.main",
                        borderColor: "primary.main",
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    {pendingAttachments.length > 0
                      ? `Add attachment (${pendingAttachments.length}/${maxFiles})`
                      : "Add attachment"}
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      accept={allowedExtensions.join(",")}
                      onChange={handleAttachmentChange}
                    />
                  </Button>

                  <Button
                    variant="contained"
                    onClick={handleSendReply}
                    disabled={!replyBody.trim() || ticketStore.replyLoading}
                    startIcon={
                      ticketStore.replyLoading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : null
                    }
                    sx={{ width: "100%" }}
                  >
                    {ticketStore.replyLoading ? "Sending..." : "Send"}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}
      </Box>
    );

    if (isMobile) {
      return (
        <SwipeableDrawer
          anchor="right"
          open={!!ticketId}
          onClose={handleClose}
          onOpen={() => {}}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: "100%",
              maxWidth: "100vw",
              display: "flex",
              flexDirection: "row",
            },
          }}
        >
          {panelContent}
        </SwipeableDrawer>
      );
    }

    return (
      <>
        <ResizePointerShim active={isSidePanelResizing} />
        <Box
          component="aside"
          sx={{
            height: "100%",
            flexShrink: 0,
            display: "flex",
            flexDirection: "row",
            width: `${sidePanelWidth}%`,
            borderLeft: `1px solid ${theme.palette.divider}`,
            backgroundColor: "background.paper",
            transition: isSidePanelResizing ? "none" : "width 150ms ease-out",
          }}
        >
          <SidebarDelimiter
            onResizeStart={handleMouseDown}
            isResizing={isSidePanelResizing}
          />
          {panelContent}
        </Box>
      </>
    );
  },
);

export default TicketDetailPanel;
